/**
 * Generates column data for a single order error row.
 * Each field becomes an EditableCell with error state, access-type
 * dependencies, and type-specific input.
 */

import { TableCellType, AccessType } from '@/types/property.types';
import {
  accessTypeOptions,
  bathRoomOptions,
  bedRoomOptions,
  orderStateOptions,
  propertyTypeOptions,
} from '@/config/options.config';
import { getEntityIdOptions } from '@/services/entity-tree';
import EditableCell, { type CellChangeData } from './EditableCell';
import type { ErrorCell } from '../../fix-errors/utils/fixErrorFieldMaps';

// ---- Types ----------------------------------------------------------------

interface OrderErrorData {
  id: number;
  services: ErrorCell;
  propertyType: ErrorCell;
  address: ErrorCell;
  unitNumber: ErrorCell;
  city: ErrorCell;
  state: ErrorCell;
  zipCode: ErrorCell;
  onsiteContactName: ErrorCell;
  onsiteContactPhone: ErrorCell;
  accessType: ErrorCell;
  lockboxLocation: ErrorCell;
  accessCode: ErrorCell;
  rentlyRegistrationPin: ErrorCell;
  entity: ErrorCell;
  orderNotes: ErrorCell;
  gateCode: ErrorCell;
  firstAvailableDate: ErrorCell;
  propertyId: ErrorCell;
  previousOrderId: ErrorCell;
  beds: ErrorCell;
  baths: ErrorCell;
  squareFeet: ErrorCell;
}

type CellConfig = {
  key: string;
  type: TableCellType;
  options?: Array<{ value: string; label: string }>;
  isOptionalField?: boolean;
  isDecimal?: boolean;
  maxCount?: number;
  isOnlyText?: boolean;
  setMaxMin?: boolean;
  /** Override isActive based on access type */
  getIsActive?: (cell: ErrorCell, accessType: string | null) => boolean;
  /** Override isDisabled based on access type */
  getIsDisabled?: (accessType: string | null) => boolean;
  /** Override value */
  getValue?: () => string | null;
};

// ---- Access type helpers --------------------------------------------------

const isLockbox = (at: string | null): boolean => at === AccessType.LOCKBOX;
const isKeypad = (at: string | null): boolean => at === AccessType.KEYPAD;
const isRently = (at: string | null): boolean => at === AccessType.RENTLY;
const isSmartAccess = (at: string | null): boolean =>
  at === 'SmartRent' || at === 'ShowMojo' || at === 'Tenant Turner';

// ---- Field config ---------------------------------------------------------

const buildOrderCellDefs = (): CellConfig[] => [
  { key: 'services', type: TableCellType.TEXT },
  { key: 'propertyType', type: TableCellType.SELECT, options: propertyTypeOptions },
  { key: 'address', type: TableCellType.TEXT },
  { key: 'unitNumber', type: TableCellType.TEXT, isOptionalField: true },
  { key: 'city', type: TableCellType.TEXT },
  { key: 'state', type: TableCellType.SELECT, options: orderStateOptions },
  { key: 'zipCode', type: TableCellType.ZIP },
  {
    key: 'onsiteContactName',
    type: TableCellType.TEXT,
    isOnlyText: true,
  },
  { key: 'onsiteContactPhone', type: TableCellType.TEXT },
  { key: 'accessType', type: TableCellType.SELECT, options: accessTypeOptions },
  {
    key: 'lockboxLocation',
    type: TableCellType.TEXT,
    getIsActive: (cell, at) => (!isLockbox(at) ? false : cell.isActive),
    getIsDisabled: (at) => !isLockbox(at),
  },
  {
    key: 'accessCode',
    type: TableCellType.TEXT,
    getIsActive: (cell, at) =>
      !(isLockbox(at) || isKeypad(at)) ? false : cell.isActive,
    getIsDisabled: (at) => !(isLockbox(at) || isKeypad(at)),
  },
  {
    key: 'rentlyRegistrationPin',
    type: TableCellType.TEXT,
    getIsActive: (cell, at) => (!isRently(at) ? false : cell.isActive),
    getIsDisabled: (at) => !isRently(at),
  },
  {
    key: 'entity',
    type: TableCellType.SELECT,
    options: getEntityIdOptions(),
  },
  { key: 'orderNotes', type: TableCellType.TEXT, isOptionalField: true },
  { key: 'gateCode', type: TableCellType.TEXT, isOptionalField: true },
  {
    key: 'firstAvailableDate',
    type: TableCellType.DATE,
    isOptionalField: true,
    setMaxMin: true,
  },
  { key: 'propertyId', type: TableCellType.TEXT, isOptionalField: true, maxCount: 50 },
  // DV-2186: numeric Legacy order id; conditional requirement enforced server-side
  { key: 'previousOrderId', type: TableCellType.TEXT, isOptionalField: true, maxCount: 12 },
  { key: 'beds', type: TableCellType.SELECT, options: bedRoomOptions, isOptionalField: true },
  { key: 'baths', type: TableCellType.SELECT, options: bathRoomOptions, isOptionalField: true },
  { key: 'squareFeet', type: TableCellType.NUMBER, isOptionalField: true, isDecimal: true },
];

// ---- Generator ------------------------------------------------------------

export const generateOrderRow = (
  data: OrderErrorData,
  setData: (change: CellChangeData) => void
): Record<string, { component: React.ReactNode; data?: string | null }> => {
  const { id } = data;
  const accessTypeValue = data.accessType.value;
  const defs = buildOrderCellDefs();
  const row: Record<string, { component: React.ReactNode; data?: string | null }> = {};

  for (const def of defs) {
    // Rows reach this generator from three parsers (upload, fix-errors, import
    // history) and from serialized state (URL/stepper) written by OLDER builds.
    // A def added after a row was built must degrade to an empty cell, not throw.
    const cell: ErrorCell =
      (data[def.key as keyof OrderErrorData] as ErrorCell | undefined) ?? {
        value: '-',
        isActive: false,
        ...(def.isOptionalField ? { isOptionalField: true } : {}),
      };
    const cellIsActive = def.getIsActive
      ? def.getIsActive(cell, accessTypeValue)
      : cell.isActive;
    const cellIsDisabled = def.getIsDisabled
      ? def.getIsDisabled(accessTypeValue)
      : false;
    const cellValue = def.getValue ? def.getValue() : cell.value;

    row[def.key] = {
      component: (
        <EditableCell
          isActive={cellIsActive}
          value={cellValue}
          errorMessage={cell.errorMessage}
          type={def.type}
          options={def.options}
          id={id}
          name={def.key}
          setData={setData}
          isOptionalField={def.isOptionalField}
          isDecimal={def.isDecimal}
          maxCount={def.maxCount}
          isOnlyText={def.isOnlyText}
          setMaxMin={def.setMaxMin}
          isDisabled={cellIsDisabled}
        />
      ),
    };
  }

  return row;
};
